/**
 * Controls Agent delegation on a Schedule Task.
 * Hides delegation without effective permission and preserves the stop control.
 * useCanDelegateToSchedulingAgent.ts combines permission and feature gates.
 */
import React from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { SparkleIcon } from '@modernloop/shared/icons';
import { getPrimaryAccentBackground, getPrimaryAccentContrastText } from '@modernloop/shared/theme';
import { Box, Chip, Link, Theme, Typography, alpha } from '@mui/material';

import { useTaskSetDelegateMutation } from 'src/generated/mloop-graphql';

import useCanDelegateToSchedulingAgent from 'src/hooks/useCanDelegateToSchedulingAgent';

import { DELEGATE_VALUE_OFFSET } from './alignment';

/** Identifies the Schedule Task and its current delegate. */
type Props = {
  taskId: string;
  delegateType: string;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TaskSetDelegateMutation = gql`
  mutation TaskSetDelegate($input: TaskSetDelegateInput!) {
    taskSetDelegate(input: $input) {
      id
      delegateType
    }
  }
`;

/** Offers delegation only with permission and permits removal of an existing Agent. */
const AiAgentDelegate: React.FC<Props> = ({ taskId, delegateType }) => {
  const { t } = useTranslation();
  const [taskSetDelegateMutation] = useTaskSetDelegateMutation();
  const canDelegate = useCanDelegateToSchedulingAgent();
  const isDelegated = delegateType === 'AGENT';

  /** Applies delegate changes while preserving the independent stop path. */
  const handleToggle = (newType: string) => {
    if (newType === 'AGENT' && !canDelegate) return;
    taskSetDelegateMutation({
      variables: { input: { taskId, delegateType: newType } },
      optimisticResponse: {
        taskSetDelegate: { id: taskId, delegateType: newType, __typename: 'Task' },
      },
    });
  };

  if (isDelegated) {
    return (
      <Chip
        data-testid="schedule-task-details-delegate-agent-chip"
        icon={
          <SparkleIcon
            sx={{ fontSize: 14, color: (theme: Theme) => `${getPrimaryAccentContrastText(theme)} !important` }}
          />
        }
        label={
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ color: (theme: Theme) => getPrimaryAccentContrastText(theme) }}
          >
            {t('hiring-dashboard-tasks:tasks.aiAgent.label')}
          </Typography>
        }
        size="small"
        onDelete={() => handleToggle('')}
        sx={{
          ml: DELEGATE_VALUE_OFFSET,
          py: 0.5,
          px: 1.5,
          height: 'auto',
          background: (theme: Theme) => getPrimaryAccentBackground(theme),
          color: (theme: Theme) => getPrimaryAccentContrastText(theme),
          '& .MuiChip-deleteIcon': {
            color: (theme: Theme) => alpha(getPrimaryAccentContrastText(theme), 0.7),
            '&:hover': { color: (theme: Theme) => getPrimaryAccentContrastText(theme) },
          },
          '& .MuiChip-icon': { color: (theme: Theme) => getPrimaryAccentContrastText(theme) },
        }}
      />
    );
  }

  if (!canDelegate) return null;

  return (
    <Box data-testid="schedule-task-details-delegate-action" sx={{ ml: DELEGATE_VALUE_OFFSET }}>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <Link
        component="button"
        variant="body2"
        underline="none"
        onClick={() => handleToggle('AGENT')}
        sx={{
          color: 'text.secondary',
          fontWeight: 400,
          '&:hover': { color: 'primary.main' },
        }}
      >
        {t('hiring-dashboard-tasks:tasks.aiAgent.delegate')}
      </Link>
    </Box>
  );
};

export default AiAgentDelegate;
